import {
  Anchor,
  Badge,
  Button,
  Chip,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AppShell } from '../components/app-shell/AppShell';
import { DigestSections, readDigestPayload } from '../components/notifications/DigestSections';
import { EmptyState, MotionSection, PageHero } from '../components/ui';
import { csrfHeaders, useAuth } from '../lib/auth-context';
import { getNotificationDisplay } from '../lib/notification-display';
import { showToast } from '../lib/toast';

type InboxItem = {
  id: string;
  kind: string;
  title: string;
  body: string;
  deepLinkUrl: string;
  createdAt: number;
  readAt: number | null;
  dismissedAt: number | null;
  /** Wave C — rich payload only present for the two digest kinds. */
  payload?: Record<string, unknown>;
};

// Digest payload + section renderer live in components/notifications/DigestSections.tsx.

type InboxResponse = {
  ok: boolean;
  items: InboxItem[];
  limit: number;
  offset: number;
};

const INBOX_KEY = ['notifications', 'inbox'] as const;

export const Route = createFileRoute('/notifications')({
  component: NotificationsPage,
});

// CFR-017: per-hat notification framing. Notifications are emitted per-user
// rather than per-hat, so a dual-role user sees host-relevant items while
// wearing the artist hat (and vice-versa). We map each `kind` to a
// hat-aligned category and let the user filter to the slice they want.
//
// Categories:
//   artist  — things you'd want to see while in the artist seat
//   host    — things you'd want to see while in the host seat
//   account — billing, verification, account closure, data export
//   all     — kinds we can't safely categorise without server-side context
//             (e.g. the user's role on a specific exchange) — disputes also
//             land here because both parties want to see them.
type NotificationCategory = 'artist' | 'host' | 'account' | 'all';

function categoriseKind(kind: string): NotificationCategory {
  if (kind.startsWith('account.') || kind.startsWith('data_export.')) return 'account';
  if (kind.startsWith('subscription.') || kind.startsWith('verification.')) return 'account';
  if (kind.startsWith('dispute.')) return 'all';
  if (kind === 'event.published') return 'host';
  if (kind === 'event.cancelled') return 'all'; // could be either side; cheap to show
  if (kind === 'event_interest.submitted') return 'host';
  if (kind === 'event_interest.accepted' || kind === 'event_interest.declined') return 'artist';
  // exchange.* and review.* — we don't carry the viewer's role on the exchange
  // in the notification payload, so we can't distinguish artist-vs-host
  // without an API change. Keep them in 'all' so they're never hidden from
  // the seat that actually needs them.
  if (kind.startsWith('exchange.') || kind.startsWith('review.')) return 'all';
  // Founder letters / marketing — show to everyone.
  return 'all';
}

type FilterValue = 'all' | 'artist' | 'host' | 'account';

function NotificationsPage() {
  const queryClient = useQueryClient();
  const { capabilities, activeHat } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: INBOX_KEY,
    queryFn: async (): Promise<InboxResponse> => {
      const res = await fetch('/api/notifications?limit=50', {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load notifications');
      return res.json();
    },
  });

  // Show the filter only when the user can actually wear more than one hat.
  // Admin alone shouldn't trigger it — they don't own artist/host content.
  const showFilter =
    capabilities !== null &&
    [capabilities.isArtist, capabilities.isHost].filter(Boolean).length > 1;

  // Default the filter to whatever the user is currently wearing — this is
  // the whole point of the chip: when you're in your host seat, you want
  // host-relevant notifications first.
  //
  // The auth context resolves async, so activeHat is often null on the first
  // render. Reading it directly into useState would freeze the filter at
  // 'all' for the rest of the session. Instead we initialise to 'all' and
  // sync once-and-only-once when activeHat arrives, tracking whether the
  // user has already clicked a chip themselves so we don't yank their
  // selection out from under them.
  const [filter, setFilter] = useState<FilterValue>('all');
  const userSetFilterRef = useRef(false);
  useEffect(() => {
    if (userSetFilterRef.current) return;
    if (activeHat === 'artist' || activeHat === 'host') {
      setFilter(activeHat);
    }
  }, [activeHat]);
  const setFilterFromUser = (v: FilterValue) => {
    userSetFilterRef.current = true;
    setFilter(v);
  };

  // Mark every visible notification read on mount + when items change. PRD
  // §5.8 says viewing the page = read.
  //
  // The handler returns `{ ok, updated }` — when the user clicked the button
  // explicitly we surface a toast with the count; the on-mount auto-pass is
  // silent (the inbox visually settles, no need to celebrate).
  const markAll = useMutation({
    mutationFn: async (): Promise<{ updated: number }> => {
      const res = await fetch('/api/notifications/read-all', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
      });
      if (!res.ok) throw new Error('Mark-all failed');
      const body = (await res.json().catch(() => ({}))) as { updated?: number };
      return { updated: typeof body.updated === 'number' ? body.updated : 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      // Also refresh the header bell badge.
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
    onError: () => showToast({ kind: 'error', body: "Couldn't mark all read." }),
  });

  // Explicit "Mark all read" button — toast on success.
  const markAllExplicit = () => {
    markAll.mutate(undefined, {
      onSuccess: ({ updated }) => {
        if (updated > 0) {
          showToast({
            kind: 'success',
            body: updated === 1 ? 'One notification marked read.' : `${updated} notifications marked read.`,
          });
        }
      },
    });
  };

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/notifications/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: csrfHeaders(),
      });
      if (!res.ok) throw new Error('Dismiss failed');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INBOX_KEY }),
    onError: () => showToast({ kind: 'error', body: "Couldn't dismiss that one." }),
  });

  // FR-043: mark only the notifications actually visible under the active
  // filter, one id at a time. The unscoped read-all would clear host-only items
  // that a dual-role user's default hat filter hides from view — marking them
  // read though they were never seen, and losing them forever behind a cleared
  // bell badge. Per-id keeps the auto-mark scoped to exactly what's on screen.
  const markVisibleRead = useMutation({
    mutationFn: async (ids: string[]): Promise<void> => {
      await Promise.all(
        ids.map((id) =>
          fetch(`/api/notifications/${encodeURIComponent(id)}/read`, {
            method: 'POST',
            credentials: 'include',
            headers: csrfHeaders(),
          }),
        ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const allItems = data?.items ?? [];
  // CFR-017: 'all' is the empty filter — no narrowing. Otherwise a kind whose
  // category is 'all' (e.g. exchange.*) stays visible under every filter so
  // we don't accidentally hide a thing that genuinely concerns the viewer.
  //
  // IMPORTANT: this hook must run on every render — keep it ABOVE the
  // conditional returns below or React will hit error #310 (hook count
  // changes between renders) the moment the query resolves.
  const items = useMemo(() => {
    if (filter === 'all') return allItems;
    return allItems.filter((it) => {
      const cat = categoriseKind(it.kind);
      return cat === filter || cat === 'all';
    });
  }, [allItems, filter]);

  // Auto-mark-read on view (PRD §5.8: viewing = read) — but only for what's
  // actually visible under the current filter (FR-043). Switching the filter
  // marks the newly-shown unread items too, which is the correct "viewing =
  // read" behaviour for exactly what the user is now looking at.
  useEffect(() => {
    const unreadVisibleIds = items.filter((i) => i.readAt === null).map((i) => i.id);
    if (unreadVisibleIds.length > 0) markVisibleRead.mutate(unreadVisibleIds);
    // markVisibleRead is stable; depend on the visible set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  if (isLoading) {
    return (
      <AppShell tone="form">
        <Loader />
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell tone="form">
        <Text c="red">We couldn’t load your notifications. Try again shortly.</Text>
      </AppShell>
    );
  }

  const grouped = groupByDay(items);

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Activity"
            title="Notifications"
            lede="Activity from your exchanges, reviews, and account."
            action={
              items.some((i) => i.readAt === null) ? (
                <Button variant="subtle" onClick={markAllExplicit} radius="md">
                  Mark all read
                </Button>
              ) : null
            }
          />
        </MotionSection>

        {showFilter && (
          <Chip.Group
            multiple={false}
            value={filter}
            onChange={(v) => {
              if (typeof v === 'string') setFilterFromUser(v as FilterValue);
            }}
          >
            <Group gap="xs">
              <Chip value="all" size="sm" variant="light">
                All
              </Chip>
              <Chip value="artist" size="sm" variant="light">
                Artist
              </Chip>
              <Chip value="host" size="sm" variant="light">
                Host
              </Chip>
              <Chip value="account" size="sm" variant="light">
                Account
              </Chip>
            </Group>
          </Chip.Group>
        )}

        {items.length === 0 ? (
          <EmptyState
            title={
              filter === 'all' || !showFilter
                ? 'No new notifications.'
                : 'Nothing in this view.'
            }
            body={
              filter === 'all' || !showFilter
                ? 'New requests, replies, and reviews land here.'
                : 'Switch filters to see other activity.'
            }
          />
        ) : (
          grouped.map((g) => (
            <Stack key={g.label} gap="xs">
              <Text size="sm" c="dimmed" fw={600}>
                {g.label}
              </Text>
              {g.items.map((n) => (
                <NotificationCard
                  key={n.id}
                  item={n}
                  onDismiss={() => dismiss.mutate(n.id)}
                />
              ))}
            </Stack>
          ))
        )}
      </Stack>
    </AppShell>
  );
}

function NotificationCard({
  item,
  onDismiss,
}: {
  item: InboxItem;
  onDismiss: () => void;
}) {
  const isUnread = item.readAt === null;
  // Wave C — digest kinds get a custom rich card.
  if (item.kind === 'network.weekly_digest' || item.kind === 'network.daily_digest') {
    return <DigestCard item={item} onDismiss={onDismiss} />;
  }
  // Voice rule: never render a raw kind id like "exchange.request_received"
  // to the user. Server-rendered title/body is preferred; if it's missing
  // (legacy rows / payload corruption) or leaked through as the raw kind,
  // fall back to the static display map.
  const display = getNotificationDisplay(item.kind);
  const title = item.title && item.title !== item.kind ? item.title : display.title;
  const body = item.body && item.body !== item.kind ? item.body : display.subtitle;
  return (
    <Stack
      gap={4}
      p="md"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 8,
        background: isUnread ? 'var(--mantine-color-gray-0)' : 'transparent',
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0, flex: 1 }}>
          <Group gap="xs" align="center">
            <Text fw={600}>{title}</Text>
            {isUnread ? (
              <Badge size="xs" color="blue" variant="light">
                New
              </Badge>
            ) : null}
          </Group>
          {body ? <Text size="sm">{body}</Text> : null}
          <Group gap="md" mt={4}>
            <Text size="xs" c="dimmed">
              {formatTime(item.createdAt)}
            </Text>
            {item.deepLinkUrl ? (
              <Anchor component={Link} to={item.deepLinkUrl} size="xs">
                Open
              </Anchor>
            ) : null}
          </Group>
        </div>
        <Button size="xs" variant="subtle" color="gray" onClick={onDismiss}>
          Dismiss
        </Button>
      </Group>
    </Stack>
  );
}

function DigestCard({ item, onDismiss }: { item: InboxItem; onDismiss: () => void }) {
  const isUnread = item.readAt === null;
  const digest = readDigestPayload(item.payload);
  const isWeekly = item.kind === 'network.weekly_digest';
  const eyebrow = isWeekly ? 'Weekly Network Notes' : 'Daily Network Notes';
  const display = getNotificationDisplay(item.kind);
  const fallbackTitle = item.title && item.title !== item.kind ? item.title : display.title;

  return (
    <Stack
      gap="sm"
      p="md"
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 12,
        background: isUnread
          ? 'linear-gradient(135deg, var(--wwaave-brand-wash) 0%, rgba(233,162,59,0.05) 100%)'
          : 'transparent',
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text
            size="xs"
            tt="uppercase"
            c="brand.7"
            fw={700}
            style={{ letterSpacing: '0.08em' }}
          >
            {eyebrow}
          </Text>
          <Group gap="xs" align="center" mt={2}>
            <Text fw={700} size="lg">
              {fallbackTitle}
            </Text>
            {isUnread ? (
              <Badge size="xs" color="brand" variant="light">
                New
              </Badge>
            ) : null}
          </Group>
          <Text size="xs" c="dimmed" mt={2}>
            {formatTime(item.createdAt)}
          </Text>
        </div>
        <Button size="xs" variant="subtle" color="gray" onClick={onDismiss}>
          Dismiss
        </Button>
      </Group>

      {digest === null ? (
        <Text size="sm" c="dimmed">
          {item.body || display.subtitle}
        </Text>
      ) : (
        <DigestSections digest={digest} />
      )}
    </Stack>
  );
}

function groupByDay(items: InboxItem[]): { label: string; items: InboxItem[] }[] {
  const groups = new Map<string, InboxItem[]>();
  for (const it of items) {
    const label = dayLabel(it.createdAt);
    const arr = groups.get(label) ?? [];
    arr.push(it);
    groups.set(label, arr);
  }
  return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
}

function dayLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime(ms: number): string {
  const deltaSec = Math.max(0, (Date.now() - ms) / 1000);
  if (deltaSec < 45) return 'just now';
  if (deltaSec < 60 * 60) {
    const m = Math.round(deltaSec / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (deltaSec < 60 * 60 * 24) {
    const h = Math.round(deltaSec / (60 * 60));
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}
